import dayjs from 'dayjs'
import { Exhibition } from 'src/models/Exhibition'
import { PromisedType } from 'src/utils/types'
import useSWR, { SWRConfiguration } from 'swr'
import { ApiClient, fetcher } from './client'
import {
  GET_EXHIBITIONS,
  getExhibition,
  getSearchExhibitions,
  POST_LIKE_HISTORIES,
} from './endpoints'
import {
  ExhibitionResponse,
  ExhibitionsResponse,
  SearchExhibitionsResponse,
  ServerExhibition,
} from './types/exhibition'

const exhibitionsFetcher = async (url: string) => {
  const res: ExhibitionsResponse = await fetcher(url)
  return {
    exhibitions: res.exhibitions.map(toExhibition),
    recommendedExhibitions: res.recommendedExhibitions.map(toExhibition),
  }
}

export const useExhibitions = (config: Partial<SWRConfiguration> = {}) =>
  useSWR<PromisedType<typeof exhibitionsFetcher>>(
    GET_EXHIBITIONS,
    exhibitionsFetcher,
    config,
  )

const allExhibitionsFetcher = async (url: string) => {
  const res: ExhibitionsResponse = await fetcher(url)
  return res.exhibitions.map(toExhibition)
}

export const useAllExhibitions = (config: Partial<SWRConfiguration> = {}) =>
  useSWR<Exhibition[]>(GET_EXHIBITIONS, allExhibitionsFetcher, config)

const toExhibition = (data: ServerExhibition): Exhibition => ({
  id: data.id,
  title: data.title,
  body: data.body,
  byAdmin: data.byAdmin,
  imageUrl: data.image.url || '',
  published: data.published,
  publishAt: dayjs(data.publishAt).format('YYYY/MM/DD'),
  companyId: data.companyId,
  exhibitionTags: data.exhibitionTags.map((tag) => ({
    name: tag.name,
    genre: tag.genre,
  })),
  companyName: data.company.name || '',
  companyAdminName: data.company.adminName || '',
  companyImageUrl:
    data.company.imageUrl?.thumb?.url || data.company.imageUrl?.url || '',
  document: data.document.url || '',
  previewDocument: data.previewDocument.url || '',
  documentTitle: data.documentTitle || '資料サンプル',
  videoUrl: data.videoUrl,
  isMatched: data.isMatched,
  likeCount: data.likeCount,
  liked: data.liked,
  isBookmarked: data.isBookmarked,
  recommended: data.recommended,
  company: {
    positionText: data.company.positionText,
  },
})

const exhibitionFetcher = async (url: string) => {
  const res: ExhibitionResponse = await fetcher(url)
  return {
    exhibition: res.exhibition ? toExhibition(res.exhibition) : undefined,
    recentExhibitions: res.recentExhibitions.map(toExhibition),
  }
}
export const useExhibition = (
  id: number,
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<PromisedType<typeof exhibitionFetcher>>(
    getExhibition(id),
    exhibitionFetcher,
    config,
  )

const searchExhibitionsFetcher = async (url: string) => {
  const res: SearchExhibitionsResponse = await fetcher(url)
  return {
    exhibitions: res.exhibitions.map(toExhibition),
    exhibitionsTotal: res.exhibitionsTotal,
  }
}
export const useSearchExhibitions = (
  tagIds?: number[],
  genre?: string,
  pageIndex: number = 1,
  orderBy?: string,
  filterBy?: string,
  keyword?: string,
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<PromisedType<typeof searchExhibitionsFetcher>>(
    getSearchExhibitions(tagIds, genre, pageIndex, orderBy, filterBy, keyword),
    searchExhibitionsFetcher,
    config,
  )

export const createLike = async (exhibitionId: number) => {
  const body = {
    exhibition_id: exhibitionId,
  }
  const res = await ApiClient.post(POST_LIKE_HISTORIES, body)
  if (res.error) return res
  return res
}
